import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Group as GroupIcon,
  Leaderboard as LeaderboardIcon,
  Logout as LogoutIcon,
  Refresh as RefreshIcon,
  Task as TaskIcon,
  VpnKey as TokenIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';

const API_BASE = '/api';
const TOKEN_KEY = 'relationship_token';

const text = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const num = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return 0;
};

const arr = <T,>(value: unknown) => (Array.isArray(value) ? (value as T[]) : []);
const normalizeToken = (value: string) => value.replace(/^Bearer\s+/i, '').trim();
const getStoredToken = () => normalizeToken(localStorage.getItem(TOKEN_KEY) || '');
const isMobileClient = () => /Android|iPhone|iPad|iPod|Mobile/i.test(window.navigator.userAgent || '');
const isWechatBrowser = () => /MicroMessenger/i.test(window.navigator.userAgent || '');
const buildWechatRedirectUrl = () => {
  const url = new URL(window.location.href);
  ['code', 'state', 'token'].forEach((key) => url.searchParams.delete(key));
  return url.toString();
};

async function api<T>(endpoint: string, options: RequestInit = {}, tokenOverride?: string): Promise<T> {
  const headers = new Headers(options.headers || {});
  if (!headers.has('Content-Type') && options.body) headers.set('Content-Type', 'application/json');
  const token = normalizeToken(tokenOverride ?? getStoredToken());
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
  let payload: any = null;
  try {
    payload = await response.json();
  } catch {}

  if (!response.ok) throw new Error(text(payload?.error, payload?.message) || `Request failed (${response.status})`);
  if (payload && typeof payload === 'object' && payload.ok === false) {
    throw new Error(text(payload.error, payload.message) || 'API returned an error');
  }
  return (payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload) as T;
}

const taskStatus = (value: unknown) => {
  const status = text(value).toLowerCase();
  if (['completed', 'done', 'finished'].includes(status)) return 'completed';
  if (['active', 'in_progress', 'ongoing'].includes(status)) return 'active';
  return 'pending';
};

const taskLabelMap: Record<string, string> = {
  housework: 'Housework',
  study: 'Study',
  work: 'Work',
  exercise: 'Exercise',
  other: 'Other',
};

const groupLabelMap: Record<string, string> = {
  family: 'Family',
  couple: 'Couple',
  friends: 'Friends',
  roommates: 'Roommates',
  teacher_student: 'Teacher / Student',
};

export default function App() {
  const [user, setUser] = useState<any | null>(null);
  const [groups, setGroups] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [points, setPoints] = useState<any>(null);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [tab, setTab] = useState(0);
  const [initializing, setInitializing] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'error' | 'success' | 'info'; text: string } | null>(null);
  const [loginToken, setLoginToken] = useState(getStoredToken());
  const [loginCode, setLoginCode] = useState('');
  const [wechatRedirectHandled, setWechatRedirectHandled] = useState(false);
  const [wechatQrDialogOpen, setWechatQrDialogOpen] = useState(false);
  const [wechatQrUrl, setWechatQrUrl] = useState('');
  const [wechatQrState, setWechatQrState] = useState('');
  const [wechatQrImage, setWechatQrImage] = useState('');
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [groupDraft, setGroupDraft] = useState({ name: '', type: 'family', description: '' });
  const [taskDraft, setTaskDraft] = useState({ title: '', description: '', category: 'housework', points: 5, receiverId: '' });
  const [taskKeyword, setTaskKeyword] = useState('');
  const [taskFilter, setTaskFilter] = useState('all');

  const loadUser = useCallback(async (tokenOverride?: string) => {
    const data = await api<any>('/users/me', {}, tokenOverride);
    const nextUser = data?.user ?? data;
    setUser({
      id: text(nextUser?.userId, nextUser?.id),
      nickname: text(nextUser?.nickname, nextUser?.name) || 'Unnamed User',
      avatar: text(nextUser?.avatarUrl, nextUser?.avatar),
      pointsTotal: num(nextUser?.pointsTotal, nextUser?.totalPoints, nextUser?.points),
    });
  }, []);

  const loadAll = useCallback(async (tokenOverride?: string) => {
    const results = await Promise.allSettled([
      loadUser(tokenOverride),
      api<any>('/groups/my').then((data) => {
        setGroups(arr<any>(data?.groups ?? data).map((item) => ({
          id: text(item?.groupId, item?.id),
          name: text(item?.groupName, item?.name) || 'Unnamed Group',
          type: text(item?.groupType, item?.type) || 'family',
          memberCount: num(item?.memberCount, item?.size, 1),
          points: num(item?.myPoints, item?.points),
          role: text(item?.myRole, item?.role) || 'Member',
          description: text(item?.description),
        })));
      }),
      api<any>('/tasks').then((data) => {
        setTasks(arr<any>(data?.tasks ?? data).map((item) => ({
          id: text(item?.taskId, item?.id),
          title: text(item?.title) || 'Untitled Task',
          description: text(item?.description),
          category: text(item?.category) || 'other',
          points: num(item?.points, item?.score, 5),
          status: taskStatus(item?.status),
        })));
      }),
      api<any>('/points/summary').then((data) => {
        setPoints(data);
        setUser((previous: any) =>
          previous
            ? { ...previous, pointsTotal: num(data?.totalPoints, data?.pointsTotal, data?.currentPoints, previous.pointsTotal) }
            : previous
        );
      }),
      api<any>('/leaderboard').then((data) => {
        setLeaderboard(arr<any>(data?.leaderboard ?? data).map((item) => ({
          id: text(item?.userId, item?.id),
          nickname: text(item?.nickname, item?.name) || 'Anonymous User',
          title: text(item?.currentTitle, item?.title) || 'Member',
          totalPoints: num(item?.totalPoints, item?.points),
        })));
      }),
    ]);

    if (results[0].status === 'rejected') {
      throw results[0].reason;
    }

    return results.filter((item) => item.status === 'rejected').length;
  }, [loadUser]);

  useEffect(() => {
    const restore = async () => {
      const token = getStoredToken();
      if (!token) {
        setInitializing(false);
        return;
      }
      try {
        await loadAll(token);
      } catch (error) {
        localStorage.removeItem(TOKEN_KEY);
        setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to restore session' });
      } finally {
        setInitializing(false);
      }
    };
    void restore();
  }, [loadAll]);

  const finishWechatCodeLogin = useCallback(async (code: string, stateOverride?: string) => {
    const data = await api<any>('/auth/wechat-login', {
      method: 'POST',
      body: JSON.stringify({
        code: code.trim(),
        state: text(stateOverride),
        redirectUri: buildWechatRedirectUrl(),
        source: isWechatBrowser() ? 'wechat_webview' : (isMobileClient() ? 'mobile_browser' : 'desktop_browser'),
      }),
    });
    const token = normalizeToken(text(data?.token, data?.accessToken, data?.sessionToken));
    if (!token) throw new Error('Login succeeded but the API did not return a token');
    localStorage.setItem(TOKEN_KEY, token);
    setLoginToken(token);
    setLoginCode('');
    setWechatQrDialogOpen(false);
    setWechatQrUrl('');
    setWechatQrState('');
    setWechatQrImage('');
    const failedCount = await loadAll(token);
    setMessage(
      failedCount > 0
        ? { type: 'info', text: `WeChat login succeeded, but ${failedCount} sections failed to refresh` }
        : { type: 'success', text: data?.linkedToPc ? 'WeChat login succeeded and synced to PC' : 'WeChat login succeeded' }
    );
  }, [loadAll]);

  useEffect(() => {
    if (!wechatQrUrl) {
      setWechatQrImage('');
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(wechatQrUrl, { width: 280, margin: 1 })
      .then((image) => {
        if (!cancelled) setWechatQrImage(image);
      })
      .catch(() => {
        if (!cancelled) setWechatQrImage('');
      });
    return () => {
      cancelled = true;
    };
  }, [wechatQrUrl]);

  useEffect(() => {
    if (!wechatQrDialogOpen || !wechatQrState || user) return;
    let cancelled = false;
    let timer: number | null = null;

    const resetPcQrLogin = () => {
      setWechatQrDialogOpen(false);
      setWechatQrUrl('');
      setWechatQrState('');
      setWechatQrImage('');
    };

    const poll = async () => {
      try {
        const data = await api<any>(`/auth/wechat-status?state=${encodeURIComponent(wechatQrState)}`);
        const status = text(data?.status).toLowerCase();
        if (status === 'authorized') {
          const token = normalizeToken(text(data?.token, data?.accessToken, data?.sessionToken));
          if (!token) throw new Error('PC authorization succeeded, but token was missing');
          localStorage.setItem(TOKEN_KEY, token);
          if (!cancelled) {
            setLoginToken(token);
            setBusy(true);
            resetPcQrLogin();
            const failedCount = await loadAll(token);
            setMessage(
              failedCount > 0
                ? { type: 'info', text: `PC QR login succeeded, but ${failedCount} sections failed to refresh` }
                : { type: 'success', text: 'PC QR login succeeded' }
            );
          }
        } else if (status === 'expired') {
          if (!cancelled) {
            resetPcQrLogin();
            setMessage({ type: 'error', text: 'QR code expired. Please start WeChat login again.' });
          }
        }
      } catch (error) {
        if (!cancelled) {
          resetPcQrLogin();
          setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to poll WeChat login status' });
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    };

    void poll();
    timer = window.setInterval(() => {
      void poll();
    }, 2000);

    return () => {
      cancelled = true;
      if (timer !== null) window.clearInterval(timer);
    };
  }, [loadAll, user, wechatQrDialogOpen, wechatQrState]);

  useEffect(() => {
    if (user || wechatRedirectHandled) return;
    const currentUrl = new URL(window.location.href);
    const token = normalizeToken(currentUrl.searchParams.get('token') || '');
    const code = (currentUrl.searchParams.get('code') || '').trim();
    const state = (currentUrl.searchParams.get('state') || '').trim();

    if (!token && !code) {
      setWechatRedirectHandled(true);
      return;
    }

    setWechatRedirectHandled(true);
    setBusy(true);
    setMessage({ type: 'info', text: 'Authorizing with WeChat...' });

    const clearWechatParams = () => {
      currentUrl.searchParams.delete('token');
      currentUrl.searchParams.delete('code');
      currentUrl.searchParams.delete('state');
      window.history.replaceState({}, document.title, currentUrl.toString());
    };

    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      void loadAll(token)
        .then((failedCount) => {
          setMessage(
            failedCount > 0
              ? { type: 'info', text: `WeChat login succeeded, but ${failedCount} sections failed to refresh` }
              : { type: 'success', text: 'WeChat login succeeded' }
          );
          clearWechatParams();
        })
        .catch((error) => {
          localStorage.removeItem(TOKEN_KEY);
          setMessage({ type: 'error', text: error instanceof Error ? error.message : 'WeChat login failed' });
        })
        .finally(() => setBusy(false));
      return;
    }

    void finishWechatCodeLogin(code, state)
      .then(() => {
        clearWechatParams();
      })
      .catch((error) => {
        localStorage.removeItem(TOKEN_KEY);
        setMessage({ type: 'error', text: error instanceof Error ? error.message : 'WeChat login failed' });
      })
      .finally(() => setBusy(false));
  }, [finishWechatCodeLogin, loadAll, user, wechatRedirectHandled]);

  const refresh = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const failedCount = await loadAll();
      setMessage(
        failedCount > 0
          ? { type: 'info', text: `Refresh finished, but ${failedCount} sections failed to refresh` }
          : { type: 'success', text: 'Refresh succeeded' }
      );
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Refresh failed' });
    } finally {
      setBusy(false);
    }
  };

  const loginByToken = async () => {
    const token = normalizeToken(loginToken);
    if (!token) {
      setMessage({ type: 'error', text: 'Please enter a valid token' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      localStorage.setItem(TOKEN_KEY, token);
      const failedCount = await loadAll(token);
      setMessage(
        failedCount > 0
          ? { type: 'info', text: `Token login succeeded, but ${failedCount} sections failed to refresh` }
          : { type: 'success', text: 'Token login succeeded' }
      );
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Token login failed' });
    } finally {
      setBusy(false);
    }
  };

  const startWechatLogin = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const redirectUri = encodeURIComponent(buildWechatRedirectUrl());
      const inWechatBrowser = isWechatBrowser();
      const mode = 'mobile';
      const data = await api<any>(`/auth/wechat-url?redirectUri=${redirectUri}&mode=${mode}`);
      const authUrl = text(data?.url, data?.authUrl, data?.loginUrl, data?.qrUrl);
      if (!authUrl) throw new Error('Backend did not return a WeChat authorization URL');
      if (inWechatBrowser) {
        window.location.href = authUrl;
        return;
      }
      setWechatQrUrl(authUrl);
      setWechatQrState(text(data?.state));
      setWechatQrDialogOpen(true);
      setMessage({
        type: 'info',
        text: isMobileClient()
          ? 'Current browser is not WeChat. Please use another device that is already logged into WeChat to scan the QR code.'
          : 'Please scan with WeChat. The PC page will finish login automatically.',
      });
      setBusy(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error
          ? `${error.message}. Please use manual Code login below`
          : 'WeChat redirect failed. Please use manual Code login below',
      });
      setBusy(false);
    }
  };

  const loginByCode = async () => {
    if (!loginCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter the WeChat authorization code' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await finishWechatCodeLogin(loginCode);
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'WeChat login failed' });
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setGroups([]);
    setTasks([]);
    setPoints(null);
    setLeaderboard([]);
    setLoginToken('');
    setWechatQrDialogOpen(false);
    setWechatQrUrl('');
    setWechatQrState('');
    setWechatQrImage('');
    setMessage({ type: 'info', text: 'Logged out' });
  };

  const createGroup = async () => {
    if (!groupDraft.name.trim()) {
      setMessage({ type: 'error', text: 'Please enter a group name' });
      return;
    }
    setBusy(true);
    try {
      await api('/groups', {
        method: 'POST',
        body: JSON.stringify({
          groupName: groupDraft.name.trim(),
          groupType: groupDraft.type,
          description: groupDraft.description.trim(),
        }),
      });
      setGroupDialogOpen(false);
      setGroupDraft({ name: '', type: 'family', description: '' });
      await loadAll();
      setMessage({ type: 'success', text: 'Group created successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to create group' });
    } finally {
      setBusy(false);
    }
  };

  const createTask = async () => {
    if (!taskDraft.title.trim()) {
      setMessage({ type: 'error', text: 'Please enter a task title' });
      return;
    }
    setBusy(true);
    try {
      await api('/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title: taskDraft.title.trim(),
          description: taskDraft.description.trim(),
          category: taskDraft.category,
          points: Math.max(1, num(taskDraft.points, 5)),
          receiverId: taskDraft.receiverId.trim() || user?.id || '',
        }),
      });
      setTaskDialogOpen(false);
      setTaskDraft({ title: '', description: '', category: 'housework', points: 5, receiverId: '' });
      await loadAll();
      setMessage({ type: 'success', text: 'Task created successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to create task' });
    } finally {
      setBusy(false);
    }
  };

  const completeTask = async (taskId: string) => {
    setBusy(true);
    try {
      const data = await api<any>(`/tasks/${taskId}/complete`, { method: 'POST' });
      await loadAll();
      setMessage({ type: 'success', text: `Task completed. Earned ${num(data?.pointsEarned, data?.points, 0)} points` });
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to complete task' });
    } finally {
      setBusy(false);
    }
  };

  const filteredTasks = useMemo(() => {
    const keyword = taskKeyword.trim().toLowerCase();
    return tasks.filter((task) => {
      if (taskFilter !== 'all' && task.status !== taskFilter) return false;
      if (!keyword) return true;
      return `${task.title} ${task.description}`.toLowerCase().includes(keyword);
    });
  }, [taskFilter, taskKeyword, tasks]);

  const metrics = useMemo(() => {
    const totalPoints = num(points?.totalPoints, points?.pointsTotal, points?.currentPoints, user?.pointsTotal);
    return [
      { label: 'Total Points', value: totalPoints, hint: 'Current cumulative contribution' },
      { label: 'This Week', value: num(points?.weeklyPoints, points?.weekPoints, points?.thisWeekPoints), hint: 'Last 7 days' },
      { label: 'This Month', value: num(points?.monthlyPoints, points?.monthPoints, points?.thisMonthPoints), hint: 'Last 30 days' },
      { label: 'Current Rank', value: num(points?.rank, points?.myRank, leaderboard.findIndex((item) => item.id === user?.id) + 1), hint: 'Leaderboard position' },
    ];
  }, [leaderboard, points, user?.id, user?.pointsTotal]);

  if (initializing) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography color="text.secondary">Restoring your session...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', px: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Card sx={{ width: '100%', maxWidth: 460 }}>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h4" align="center" gutterBottom>Relationship Tasks</Typography>
                <Typography variant="body2" color="text.secondary" align="center">Log in with a backend token, a WeChat code, or a QR scan.</Typography>
              </Box>
              {message && <Alert severity={message.type}>{message.text}</Alert>}
              <TextField label="Access Token" value={loginToken} onChange={(e) => setLoginToken(e.target.value)} placeholder="Paste a backend-issued token" fullWidth />
              <Button variant="contained" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <TokenIcon />} onClick={loginByToken} disabled={busy}>Login with Token</Button>
              <Button variant="contained" color="success" onClick={startWechatLogin} disabled={busy}>
                WeChat Login / PC QR Login</Button>
              <TextField label="WeChat Code" value={loginCode} onChange={(e) => setLoginCode(e.target.value)} placeholder="Paste the code returned by WeChat" fullWidth />
              <Button variant="outlined" onClick={loginByCode} disabled={busy}>Login with Code</Button>
            </Stack>
          </CardContent>
        </Card>
        <Dialog
          open={wechatQrDialogOpen}
          onClose={() => {
            if (busy) return;
            setWechatQrDialogOpen(false);
            setWechatQrUrl('');
            setWechatQrState('');
            setWechatQrImage('');
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>WeChat QR Login</DialogTitle>
          <DialogContent>
            <Stack spacing={2} alignItems="center" sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                Scan this QR code in WeChat. The PC page will finish login automatically after authorization.</Typography>
              {wechatQrImage ? (
                <Box component="img" src={wechatQrImage} alt="WeChat QR Code" sx={{ width: 280, maxWidth: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider' }} />
              ) : (
                <Box sx={{ width: 280, height: 280, display: 'grid', placeItems: 'center' }}>
                  <CircularProgress />
                </Box>
              )}
              <TextField
                label="Authorization URL"
                value={wechatQrUrl}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText="If scanning is inconvenient, copy this URL and open it inside WeChat to continue authorization."
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setWechatQrDialogOpen(false);
                setWechatQrUrl('');
                setWechatQrState('');
                setWechatQrImage('');
              }}
            >
              Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Relationship Tasks</Typography>
          <Button color="inherit" startIcon={<RefreshIcon />} onClick={refresh} disabled={busy}>Refresh</Button>
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar src={user.avatar}>{user.nickname.charAt(0)}</Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2">{user.nickname}</Typography>
              <Typography variant="caption">Points: {user.pointsTotal}</Typography>
            </Box>
          </Stack>
          <IconButton color="inherit" onClick={logout}><LogoutIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {message && <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}><Card><CardContent><Stack direction="row" spacing={2} alignItems="center"><Avatar sx={{ bgcolor: 'primary.main' }}><GroupIcon /></Avatar><Box><Typography variant="body2" color="text.secondary">Groups</Typography><Typography variant="h5">{groups.length}</Typography></Box></Stack></CardContent></Card></Grid>
          <Grid item xs={12} sm={6} md={3}><Card><CardContent><Stack direction="row" spacing={2} alignItems="center"><Avatar sx={{ bgcolor: 'warning.main' }}><TaskIcon /></Avatar><Box><Typography variant="body2" color="text.secondary">Open Tasks</Typography><Typography variant="h5">{tasks.filter((item) => item.status !== 'completed').length}</Typography></Box></Stack></CardContent></Card></Grid>
          <Grid item xs={12} sm={6} md={3}><Card><CardContent><Stack direction="row" spacing={2} alignItems="center"><Avatar sx={{ bgcolor: 'success.main' }}><CheckCircleIcon /></Avatar><Box><Typography variant="body2" color="text.secondary">Completed Tasks</Typography><Typography variant="h5">{tasks.filter((item) => item.status === 'completed').length}</Typography></Box></Stack></CardContent></Card></Grid>
          <Grid item xs={12} sm={6} md={3}><Card><CardContent><Stack direction="row" spacing={2} alignItems="center"><Avatar sx={{ bgcolor: 'secondary.main' }}><LeaderboardIcon /></Avatar><Box><Typography variant="body2" color="text.secondary">Total Points</Typography><Typography variant="h5">{num(points?.totalPoints, points?.pointsTotal, user.pointsTotal)}</Typography></Box></Stack></CardContent></Card></Grid>
        </Grid>

        <Paper sx={{ mt: 3, p: 1 }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
            <Tab label="Groups" />
            <Tab label="Tasks" />
            <Tab label="Points" />
          </Tabs>
        </Paper>

        {tab === 0 && (
          <Box sx={{ mt: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
              <Box><Typography variant="h5">My Groups</Typography><Typography variant="body2" color="text.secondary">See your relationship groups and their current status.</Typography></Box>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGroupDialogOpen(true)}>Create Group</Button>
            </Stack>
            {groups.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6" gutterBottom>No groups yet</Typography><Button variant="contained" onClick={() => setGroupDialogOpen(true)}>Create your first group</Button></Paper>
            ) : (
              <Grid container spacing={3}>
                {groups.map((group) => (
                  <Grid item xs={12} md={6} key={group.id || group.name}>
                    <Card className="shadow-card">
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: 'primary.main' }}><GroupIcon /></Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h6">{group.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{groupLabelMap[group.type] || group.type} 闂?{group.memberCount} 濠?闂?{group.role}</Typography>
                            </Box>
                            <Chip label={`${group.points} pts`} color="primary" />
                          </Stack>
                          {group.description && <Typography variant="body2" color="text.secondary">{group.description}</Typography>}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ mt: 3 }}>
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2}>
                <Box><Typography variant="h5">Task Board</Typography><Typography variant="body2" color="text.secondary">Track pending work, filter by status, and complete tasks for points.</Typography></Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setTaskDialogOpen(true)}>Create Task</Button>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}><TextField fullWidth label="Search tasks" value={taskKeyword} onChange={(e) => setTaskKeyword(e.target.value)} /></Grid>
                <Grid item xs={12} md={6}>
                  <TextField select fullWidth label="Task status" value={taskFilter} onChange={(e) => setTaskFilter(e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Stack>
            {filteredTasks.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6" gutterBottom>No tasks found</Typography><Button variant="contained" onClick={() => setTaskDialogOpen(true)}>Create a task</Button></Paper>
            ) : (
              <Grid container spacing={3}>
                {filteredTasks.map((task) => (
                  <Grid item xs={12} key={task.id || task.title}>
                    <Card className="shadow-card">
                      <CardContent>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6">{task.title}</Typography>
                            {task.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{task.description}</Typography>}
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                              <Chip label={taskLabelMap[task.category] || task.category} size="small" />
                              <Chip label={`${task.points} pts`} size="small" color="primary" variant="outlined" />
                              <Chip label={task.status === 'completed' ? 'Completed' : task.status === 'active' ? 'Active' : 'Pending'} size="small" color={task.status === 'completed' ? 'success' : task.status === 'active' ? 'warning' : 'default'} variant="outlined" />
                            </Stack>
                          </Box>
                          {task.status !== 'completed' && <Button variant="contained" onClick={() => completeTask(task.id)} disabled={busy}>Complete</Button>}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>Points Overview</Typography>
            <Grid container spacing={2}>
              {metrics.map((metric) => (
                <Grid item xs={12} sm={6} md={3} key={metric.label}>
                  <Card><CardContent><Typography variant="body2" color="text.secondary">{metric.label}</Typography><Typography variant="h4" sx={{ my: 1 }}>{metric.label === 'Current Rank' ? `#${metric.value || '--'}` : metric.value}</Typography><Typography variant="caption" color="text.secondary">{metric.hint}</Typography></CardContent></Card>
                </Grid>
              ))}
            </Grid>
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Leaderboard</Typography>
                {leaderboard.length === 0 ? (
                  <Typography color="text.secondary">No leaderboard data yet</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {leaderboard.map((item, index) => (
                      <Paper key={item.id || item.nickname} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Chip label={`#${index + 1}`} color={index === 0 ? 'primary' : 'default'} size="small" />
                          <Avatar>{item.nickname.charAt(0)}</Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1">{item.nickname}</Typography>
                            <Typography variant="caption" color="text.secondary">{item.title}</Typography>
                          </Box>
                          <Typography variant="subtitle1">{item.totalPoints}</Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Container>

      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Group</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField autoFocus label="Group Name" value={groupDraft.name} onChange={(e) => setGroupDraft({ ...groupDraft, name: e.target.value })} fullWidth />
            <TextField select label="Group Type" value={groupDraft.type} onChange={(e) => setGroupDraft({ ...groupDraft, type: e.target.value })} fullWidth>
              {Object.entries(groupLabelMap).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField label="Description" value={groupDraft.description} onChange={(e) => setGroupDraft({ ...groupDraft, description: e.target.value })} multiline minRows={3} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
          <Button onClick={createGroup} variant="contained" disabled={busy}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Task</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField autoFocus label="Task Title" value={taskDraft.title} onChange={(e) => setTaskDraft({ ...taskDraft, title: e.target.value })} fullWidth />
            <TextField label="Task Description" value={taskDraft.description} onChange={(e) => setTaskDraft({ ...taskDraft, description: e.target.value })} multiline minRows={3} fullWidth />
            <TextField select label="Category" value={taskDraft.category} onChange={(e) => setTaskDraft({ ...taskDraft, category: e.target.value })} fullWidth>
              {Object.entries(taskLabelMap).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </TextField>
            <TextField label="Points" type="number" value={taskDraft.points} onChange={(e) => setTaskDraft({ ...taskDraft, points: Math.max(1, Number(e.target.value) || 1) })} fullWidth />
            <TextField label="Receiver ID" value={taskDraft.receiverId} onChange={(e) => setTaskDraft({ ...taskDraft, receiverId: e.target.value })} helperText="Leave empty to default to the current user." fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
          <Button onClick={createTask} variant="contained" disabled={busy}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


